import { useCallback, useEffect, useState } from 'react';

export type AsyncState<T, TInitial = undefined> = {
  loading: boolean;
  error: Error | undefined;
  data: TInitial extends undefined ? T | undefined : T;
  idle: boolean;
  fetched: boolean;
  invalidate: () => void;
  stopRefetching: () => void;
};

type UseAsyncOptions<T, TInitial = undefined> = {
  initialValue?: TInitial;
  keepPrevious?: boolean;
  enabled?: boolean;
  refetchInterval?: number;
  onSuccess?: (data: T, actions: { stopRefetching: () => void; resetData: () => void }) => void;
};

export function useAsync<T, TInitial = undefined>(
  callback: () => Promise<T>,
  dependencies: ReadonlyArray<any> = [],
  options?: UseAsyncOptions<T, TInitial>
): AsyncState<T, TInitial> {
  const { initialValue, keepPrevious = false, enabled = true, refetchInterval, onSuccess } = options || {};
  const [isValid, setIsValid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetched, setFetched] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<T>(initialValue as T);
  const [idle, setIdle] = useState<boolean>(true);
  const [stopRefetchingState, setStopRefetchingState] = useState<boolean>(false);

  const resetData = useCallback(() => {
    setData(initialValue as T);
  }, [initialValue]);

  const callbackMemoized = useCallback(() => {
    setLoading(true);
    setError(undefined);
    if (!keepPrevious) {
      setData(initialValue as T);
    }
    setFetched(false);
    setIdle(false);
    callback()
      .then((result) => {
        setData(result);
        setFetched(true);

        if (onSuccess) {
          onSuccess(result, { stopRefetching, resetData });
        }
      })
      .catch((e) => {
        setError(e);
        setFetched(true);
      })
      .finally(() => {
        setLoading(false);
        setIsValid(true);
        setFetched(true);
      });
  }, [isValid, ...dependencies]);

  useEffect(() => {
    if (enabled) {
      callbackMemoized();
    }
  }, [callbackMemoized, enabled]);

  useEffect(() => {
    return () => {
      setStopRefetchingState(false);
    };
  }, [...dependencies]);

  // Refetch interval effect
  useEffect(() => {
    if (!enabled || !refetchInterval || stopRefetchingState) {return;}

    const interval = setInterval(() => {
      callbackMemoized();
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [callbackMemoized, enabled, refetchInterval, stopRefetchingState]);

  const invalidate = useCallback(() => {
    setIsValid(false);
  }, []);

  const stopRefetching = useCallback(() => {
    setStopRefetchingState(true);
  }, []);

  return { loading, error, data, idle, fetched, invalidate, stopRefetching };
}
